import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import api from '../lib/api';

const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const DAY_NAMES   = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

const TIME_OPTIONS = [];
for (let h = 6; h <= 23; h++) {
  for (const m of [0, 30]) {
    if (h === 23 && m === 30) break;
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12  = h % 12 || 12;
    const label = `${h12}:${String(m).padStart(2,'0')} ${ampm}`;
    const value = `${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}`;
    TIME_OPTIONS.push({ value, label });
  }
}

function buildCalDays(year, month) {
  const first = new Date(year, month, 1);
  const last  = new Date(year, month + 1, 0);
  const start = new Date(first);
  start.setDate(first.getDate() - first.getDay());
  const end = new Date(last);
  end.setDate(last.getDate() + (6 - last.getDay()));
  const days = [], cur = new Date(start);
  while (cur <= end) { days.push(new Date(cur)); cur.setDate(cur.getDate() + 1); }
  return days;
}

function dateKey(d) {
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}

// ── Mini date picker dropdown ─────────────────────────────────────────────────
function MiniDatePicker({ value, onChange, placeholder = 'Select date' }) {
  const [open,      setOpen]      = useState(false);
  const [dropPos,   setDropPos]   = useState({ top: 0, left: 0 });
  const [viewYear,  setViewYear]  = useState(() => { const d = value ? new Date(value + 'T00:00:00') : new Date(); return d.getFullYear(); });
  const [viewMonth, setViewMonth] = useState(() => { const d = value ? new Date(value + 'T00:00:00') : new Date(); return d.getMonth(); });
  const btnRef  = useRef(null);
  const dropRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = e => {
      if (btnRef.current?.contains(e.target) || dropRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  function toggle() {
    if (!open && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      setDropPos({ top: r.bottom + 4, left: r.left });
    }
    setOpen(o => !o);
  }

  function prevMonth() { if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y-1); } else setViewMonth(m => m-1); }
  function nextMonth() { if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y+1); } else setViewMonth(m => m+1); }

  const days         = buildCalDays(viewYear, viewMonth);
  const todayKey     = dateKey(new Date());
  const selectedKey  = value || '';
  const displayLabel = value
    ? new Date(value + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : placeholder;

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={toggle}
        className="form-input"
        style={{ textAlign: 'left', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', color: value ? 'var(--tx-primary)' : 'var(--tx-faint)' }}
      >
        {displayLabel}
        <span style={{ color: 'var(--tx-muted)', fontSize: 11, marginLeft: 8 }}>▾</span>
      </button>

      {open && (
        <div ref={dropRef} style={{
          position: 'fixed', top: dropPos.top, left: dropPos.left, zIndex: 10000,
          background: 'var(--bg-card)', border: '1px solid var(--border)',
          borderRadius: 'var(--radius-md)', padding: '10px 12px', width: 230,
          boxShadow: '0 6px 28px rgba(0,0,0,0.5)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <button type="button" className="cal-nav-btn" onClick={prevMonth}>‹</button>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--tx-primary)' }}>{MONTH_NAMES[viewMonth]} {viewYear}</div>
            <button type="button" className="cal-nav-btn" onClick={nextMonth}>›</button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 1 }}>
            {['S','M','T','W','T','F','S'].map((d, i) => (
              <div key={i} style={{ textAlign: 'center', fontSize: 9, color: 'var(--tx-faint)', fontWeight: 600, padding: '3px 0' }}>{d}</div>
            ))}
            {days.map(day => {
              const key     = dateKey(day);
              const isToday = key === todayKey;
              const isSel   = key === selectedKey;
              const other   = day.getMonth() !== viewMonth;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => { if (!other) { onChange(key); setOpen(false); } }}
                  style={{
                    padding: '5px 0', fontSize: 11, borderRadius: 4, border: 'none', fontFamily: 'var(--font)',
                    cursor: other ? 'default' : 'pointer',
                    background: isSel ? 'var(--violet)' : isToday ? 'rgba(139,109,255,0.2)' : 'transparent',
                    color: isSel ? '#fff' : isToday ? 'var(--violet)' : other ? 'var(--tx-faint)' : 'var(--tx-primary)',
                    fontWeight: isSel || isToday ? 600 : 400,
                  }}
                >
                  {day.getDate()}
                </button>
              );
            })}
          </div>
          {value && (
            <button
              type="button"
              onClick={() => { onChange(''); setOpen(false); }}
              style={{ marginTop: 8, fontSize: 11, color: 'var(--tx-muted)', background: 'none', border: 'none', cursor: 'pointer', width: '100%', textAlign: 'center', padding: '3px 0', fontFamily: 'var(--font)' }}
            >
              Clear
            </button>
          )}
        </div>
      )}
    </>
  );
}

// ── Availability modal ────────────────────────────────────────────────────────
// `talentId` is only passed when staff is editing on behalf of a talent; talent
// users editing their own availability omit it and the API uses their JWT sub.
function AvailabilityModal({ dateStr, existing, talentId, onClose, onSaved, onDeleted }) {
  const isEdit = Boolean(existing);
  // 'block' entries auto-display as 'custom' so users can edit start/end freely
  const [avType, setAvType] = useState(() => {
    const t = existing?.availabilityType || 'full_day';
    return t === 'block' ? 'custom' : t;
  });
  const [blockStart, setBlockStart] = useState(existing?.blockStart || '09:00');
  const [blockEnd, setBlockEnd] = useState(() => {
    if (existing?.blockEnd) return existing.blockEnd;
    if (existing?.blockStart) {
      const [h, min] = existing.blockStart.split(':').map(Number);
      return `${String(h + 2).padStart(2,'0')}:${String(min).padStart(2,'0')}`;
    }
    return '11:00';
  });
  const [isRecurring, setIsRecurring] = useState(existing?.isRecurring || false);
  const [recurUntil,  setRecurUntil]  = useState(
    existing?.recurrenceEndDate ? existing.recurrenceEndDate.slice(0,10) : ''
  );
  const [saving, setSaving]  = useState(false);
  const [err,    setErr]     = useState('');
  const [delMode, setDelMode] = useState(null); // null | 'occurrence' | 'future' | 'all'

  const [y, m, d] = dateStr.split('-').map(Number);
  const prettyDate = new Date(y, m-1, d).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });

  async function handleSave() {
    setSaving(true); setErr('');
    try {
      const hasBlock = avType === 'block' || avType === 'custom';
      const payload = {
        date:             dateStr,
        availabilityType: avType,
        blockStart:       hasBlock ? blockStart : undefined,
        blockEnd:         avType === 'custom' ? blockEnd : undefined,
        isRecurring,
        recurrenceEndDate: isRecurring && recurUntil ? recurUntil : undefined,
      };
      if (isEdit) {
        await api.patch(`/api/talent-availability/${existing.id}`, payload);
      } else {
        await api.post('/api/talent-availability', talentId ? { ...payload, talentId } : payload);
      }
      onSaved();
    } catch (e) {
      setErr(e.response?.data?.error || 'Save failed.');
    } finally { setSaving(false); }
  }

  async function handleDelete(mode) {
    setSaving(true); setErr('');
    try {
      const params = mode === 'occurrence' ? `?mode=occurrence&date=${dateStr}`
                   : mode === 'future'     ? `?mode=future&date=${dateStr}`
                   : '';
      await api.delete(`/api/talent-availability/${existing.id}${params}`);
      onDeleted();
    } catch (e) {
      setErr(e.response?.data?.error || 'Delete failed.');
    } finally { setSaving(false); setDelMode(null); }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ width: 420 }}>
        <div className="modal-header">
          <div className="modal-title">{isEdit ? 'Edit Availability' : 'Mark Availability'}</div>
          <button className="vc-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <div style={{ fontSize: 12, color: 'var(--tx-muted)', marginBottom: 16 }}>{prettyDate}</div>
          {err && <div className="form-error" style={{ marginBottom: 12 }}>{err}</div>}

          {delMode && (
            <div style={{ padding: '14px', background: 'rgba(42,14,14,0.5)', border: '1px solid rgba(212,85,85,0.2)', borderRadius: 10, marginBottom: 16 }}>
              <div style={{ fontSize: 13, color: 'var(--tx-primary)', marginBottom: 12 }}>
                {delMode === 'occurrence'
                  ? `Remove just ${prettyDate}?`
                  : `Remove ${prettyDate} and all future occurrences?`}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn-ghost" style={{ fontSize: 12 }} onClick={() => setDelMode(null)}>Cancel</button>
                <button
                  style={{ padding: '7px 16px', background: 'rgba(212,85,85,0.15)', color: 'var(--s-incomplete-t)', border: '1px solid rgba(212,85,85,0.35)', borderRadius: 'var(--radius-sm)', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font)' }}
                  onClick={() => handleDelete(delMode)}
                  disabled={saving}
                >
                  {saving ? 'Removing…' : 'Confirm Remove'}
                </button>
              </div>
            </div>
          )}

          <label className="form-label">Availability type</label>
          <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
            {['full_day', 'custom'].map(v => (
              <button
                key={v}
                onClick={() => setAvType(v)}
                style={{
                  padding: '7px 18px', fontSize: 12, borderRadius: 'var(--radius-sm)', cursor: 'pointer', fontFamily: 'var(--font)', fontWeight: avType === v ? 600 : 400,
                  background: avType === v ? 'var(--violet)' : 'var(--bg-card)',
                  color: avType === v ? '#fff' : 'var(--tx-secondary)',
                  border: avType === v ? '1px solid var(--violet)' : '1px solid var(--border)',
                }}
              >
                {v === 'full_day' ? 'All day' : 'Custom times'}
              </button>
            ))}
          </div>

          {avType === 'custom' && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label className="form-label">Start time</label>
                  <select className="form-input" value={blockStart} onChange={e => setBlockStart(e.target.value)}>
                    {TIME_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="form-label">End time</label>
                  <select className="form-input" value={blockEnd} onChange={e => setBlockEnd(e.target.value)}>
                    {TIME_OPTIONS.filter(o => o.value > blockStart).map(o => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              {blockEnd <= blockStart && (
                <div style={{ fontSize: 11, color: 'var(--s-incomplete-t)', marginTop: 6 }}>End time must be after start time.</div>
              )}
            </div>
          )}

          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: isRecurring ? 12 : 0 }}>
            <input
              type="checkbox" id="recur"
              checked={isRecurring}
              onChange={e => setIsRecurring(e.target.checked)}
              style={{ width: 16, height: 16 }}
            />
            <label htmlFor="recur" style={{ fontSize: 13, color: 'var(--tx-primary)', cursor: 'pointer' }}>
              Repeat weekly
            </label>
          </div>

          {isRecurring && (
            <div style={{ marginTop: 8 }}>
              <label className="form-label">Repeat until (optional)</label>
              <MiniDatePicker
                value={recurUntil}
                onChange={setRecurUntil}
                placeholder="Repeat indefinitely"
              />
              <div style={{ fontSize: 11, color: 'var(--tx-muted)', marginTop: 4 }}>Leave blank to repeat indefinitely.</div>
            </div>
          )}
        </div>

        <div className="modal-footer" style={{ justifyContent: 'space-between' }}>
          <div>
            {isEdit && (
              <div style={{ display: 'flex', gap: 8 }}>
                {existing.isRecurring ? (
                  <>
                    <button
                      style={{ padding: '7px 12px', background: 'none', color: 'var(--s-incomplete-t)', border: '1px solid rgba(212,85,85,0.35)', borderRadius: 'var(--radius-sm)', fontSize: 11, cursor: 'pointer', fontFamily: 'var(--font)' }}
                      onClick={() => setDelMode('occurrence')}
                    >
                      Remove this date
                    </button>
                    <button
                      style={{ padding: '7px 12px', background: 'none', color: 'var(--s-incomplete-t)', border: '1px solid rgba(212,85,85,0.35)', borderRadius: 'var(--radius-sm)', fontSize: 11, cursor: 'pointer', fontFamily: 'var(--font)' }}
                      onClick={() => setDelMode('future')}
                    >
                      Remove all future
                    </button>
                  </>
                ) : (
                  <button
                    style={{ padding: '7px 14px', background: 'none', color: 'var(--s-incomplete-t)', border: '1px solid rgba(212,85,85,0.35)', borderRadius: 'var(--radius-sm)', fontSize: 12, cursor: 'pointer', fontFamily: 'var(--font)' }}
                    onClick={() => handleDelete('all')}
                    disabled={saving}
                  >
                    Remove
                  </button>
                )}
              </div>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn-ghost" onClick={onClose}>Cancel</button>
            <button className="btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : isEdit ? 'Update' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Reusable view ─────────────────────────────────────────────────────────────
// Renders the month grid + AvailabilityModal. When `talentId` is provided
// (staff editing on a talent's behalf) the API calls are scoped to that talent.
export function TalentAvailabilityView({ talentId = null }) {
  const now    = new Date();
  const [year,  setYear]  = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth());
  const [rows,  setRows]  = useState([]);
  const [modal, setModal] = useState(null); // null | { dateStr, existing? }

  const loadRows = useCallback(async () => {
    try {
      const res = await api.get('/api/talent-availability', talentId ? { params: { talentId } } : undefined);
      setRows(res.data);
    } catch { /* silent */ }
  }, [talentId]);

  useEffect(() => { loadRows(); }, [loadRows]);

  const days = useMemo(() => buildCalDays(year, month), [year, month]);

  // Build a map: dateKey → [rows]  (handles recurring entries for the visible month)
  const byDate = useMemo(() => {
    const map = {};
    const monthDays = days.filter(d => d.getMonth() === month);
    for (const day of monthDays) {
      const key = dateKey(day);
      const [y2, m2, d2] = key.split('-').map(Number);
      const dow = new Date(Date.UTC(y2, m2-1, d2)).getUTCDay();
      const matches = rows.filter(r => {
        const rDate = r.date?.slice(0, 10);
        if (!r.isRecurring) return rDate === key;
        const startDate = rDate;
        if (startDate > key) return false;
        if (r.recurrenceEndDate && r.recurrenceEndDate.slice(0,10) < key) return false;
        return r.recurrenceDayOfWeek === dow;
      });
      if (matches.length > 0) map[key] = matches;
    }
    return map;
  }, [rows, days, month]);

  function prevMonth() {
    if (month === 0) { setMonth(11); setYear(y => y-1); } else setMonth(m => m-1);
  }
  function nextMonth() {
    if (month === 11) { setMonth(0); setYear(y => y+1); } else setMonth(m => m+1);
  }

  function handleDayClick(key) {
    const existing = byDate[key]?.[0] || null;
    setModal({ dateStr: key, existing });
  }

  const todayKey = dateKey(now);

  return (
    <div className="cal-grid-wrap">
          <div className="cal-month-nav">
            <button className="cal-nav-btn" onClick={prevMonth}>‹</button>
            <div className="cal-month-label">{MONTH_NAMES[month]} {year}</div>
            <button className="cal-nav-btn" onClick={nextMonth}>›</button>
          </div>

          <div className="cal-grid">
            {DAY_NAMES.map(d => <div key={d} className="cal-dow">{d}</div>)}
            {days.map(day => {
              const key       = dateKey(day);
              const isToday   = key === todayKey;
              const otherMo   = day.getMonth() !== month;
              const avRows    = byDate[key] || [];
              const hasAv     = avRows.length > 0;
              const isRecur   = avRows.some(r => r.isRecurring);

              return (
                <div
                  key={key}
                  className={`cal-day ${isToday ? 'today' : ''} ${otherMo ? 'other-month' : ''}`}
                  style={{ cursor: 'pointer', background: hasAv ? 'rgba(139,109,255,0.1)' : undefined, border: hasAv ? '1px solid rgba(139,109,255,0.25)' : undefined }}
                  onClick={() => !otherMo && handleDayClick(key)}
                >
                  <div className="cal-day-num" style={{ color: hasAv ? 'var(--violet)' : undefined }}>
                    {day.getDate()}
                    {isRecur && <span style={{ marginLeft: 3, fontSize: 9 }} title="Recurring">↻</span>}
                  </div>
                  {hasAv && (
                    <div style={{ marginTop: 4 }}>
                      {avRows.map(r => (
                        <div key={r.id} style={{ fontSize: 10, color: 'var(--violet)', fontWeight: 500, lineHeight: 1.3 }}>
                          {r.availabilityType === 'full_day'
                            ? 'All day'
                            : `${r.blockStart ? r.blockStart.slice(0,5) : ''}–${r.blockEnd ? r.blockEnd.slice(0,5) : ''}`}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

      {/* Legend */}
      <div style={{ marginTop: 16, display: 'flex', gap: 20, fontSize: 11, color: 'var(--tx-muted)' }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ width: 12, height: 12, borderRadius: 3, background: 'rgba(139,109,255,0.25)', display: 'inline-block' }} />
          Available
        </span>
        <span>↻ = Repeating weekly</span>
      </div>

      {modal && (
        <AvailabilityModal
          dateStr={modal.dateStr}
          existing={modal.existing}
          talentId={talentId}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); loadRows(); }}
          onDeleted={() => { setModal(null); loadRows(); }}
        />
      )}
    </div>
  );
}

// ── Main page (talent viewing own availability) ──────────────────────────────
export default function TalentAvailabilityCalendar() {
  return (
    <div className="main">
      <div className="topbar">
        <div>
          <div className="page-title">My Availability</div>
          <div className="page-sub">Click any date to mark or edit your availability</div>
        </div>
      </div>

      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '20px 36px 32px' }}>
        <TalentAvailabilityView />
      </div>
    </div>
  );
}
